import { timer } from 'rxjs';
import { mergeMap, takeWhile } from 'rxjs/operators';
import { SENTRY_RECURRING_REQUESTS_IGNORE_STATUS_CODES, UNKNOWN_HTTP_FAILURE_RESPONSE_REGEX, } from 'Angular/constants/sentry.constants';
import { YChartsSentryUtils } from 'Angular/services/sentry/sentry-utils';
/**
 * This is a util function to make a recurring request.
 *
 * @param intervalTime: How much time (in ms) to wait before making the next request.
 * @param fetcher: The function that is responsible for fetching data. IMPORTANT NOTE: since we are passing `fetcher`
 *                 as a first class function, pay careful attention to make sure the correct context for `this` is
 *                 being established. Do not solve this issue by making methods in services arrow functions.
 *                 https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Functions/Arrow_functions
 * @param fetchCallback: The callback to run on the data that has been fetched.
 * @param isAlive: Whether the component making the requests is alive (i.e. not yet destroyed).
 */
export function makeRecurringRequests(intervalTime, fetcher, fetchCallback, isAlive) {
    return timer(0, intervalTime) // Use timer instead of interval so that it emits immediately.
        .pipe(takeWhile(() => isAlive), mergeMap(() => fetcher()))
        .subscribe({
        next: (data) => {
            fetchCallback(data);
        },
        error: (error) => {
            // For recurring requests, we sometimes get HTTP errors when users leave the page
            // open for a long time. In this case catch and ignore the error.
            // Also ignore 403 errors because they are likely caused by a user leaving the page open until they are logged out,
            // and ignore 502 and 503 errors since they often happen when a deployment is in progress.
            if (!error.message.match(UNKNOWN_HTTP_FAILURE_RESPONSE_REGEX) &&
                !SENTRY_RECURRING_REQUESTS_IGNORE_STATUS_CODES.has(error.status)) {
                YChartsSentryUtils.captureException(error);
            }
        },
    });
}
